import { useCallback, useEffect, useRef, useState } from "react";

/** What to capture in, best first.
 *
 * Chosen for capture quality alone - everything is re-encoded to MP3 before it
 * is sent, because WhatsApp processes neither of the containers a browser will
 * actually record. Opus in WebM is what Chrome does best.
 */
const CANDIDATE_TYPES = [
  "audio/webm;codecs=opus",
  "audio/webm",
  "audio/mp4",
  "audio/ogg;codecs=opus",
];

const supportedType = () => {
  if (typeof MediaRecorder === "undefined") {
    return undefined;
  }

  return CANDIDATE_TYPES.find((type) => MediaRecorder.isTypeSupported(type));
};

/** Past this a "voice note" is really a file, and WhatsApp caps audio at 16 MB
 * anyway.
 */
export const MAX_RECORDING_SECONDS = 5 * 60;

export const useVoiceRecorder = (params: {
  onFinish: (file: File) => void;
  onError: (error: unknown) => void;
}) => {
  const { onFinish, onError } = params;

  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  // Reads how loud the microphone is, for the bars shown while recording
  const [analyser, setAnalyser] = useState<AnalyserNode | null>(null);

  // The caller passes inline arrows, so these are held in refs to keep
  // start/stop/cancel stable - a cancel that changes every render would abort
  // the recording it is meant to be guarding
  const onFinishRef = useRef(onFinish);
  const onErrorRef = useRef(onError);

  useEffect(() => {
    onFinishRef.current = onFinish;
    onErrorRef.current = onError;
  });

  const recorderRef = useRef<MediaRecorder | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Decided before stop(), so the stop handler knows whether to send or bin it
  const keepRef = useRef(false);

  const mimeType = supportedType();

  const cleanup = useCallback(() => {
    if (tickRef.current) {
      clearInterval(tickRef.current);
      tickRef.current = null;
    }

    // Releases the microphone; without this the browser keeps showing the tab
    // as recording
    recorderRef.current?.stream.getTracks().forEach((track) => track.stop());
    recorderRef.current = null;
    chunksRef.current = [];

    audioContextRef.current?.close().catch(() => undefined);
    audioContextRef.current = null;

    setRecording(false);
    setSeconds(0);
    setAnalyser(null);
  }, []);

  const stop = useCallback(
    (keep: boolean) => {
      keepRef.current = keep;

      const recorder = recorderRef.current;

      if (!recorder || recorder.state === "inactive") {
        cleanup();
        return;
      }

      // onstop fires after the last chunk has been delivered, so the file is
      // assembled there rather than here
      recorder.stop();
    },
    [cleanup],
  );

  const start = useCallback(async () => {
    // Not component state, which is a render behind: a second click in the
    // same tick would open a second microphone stream
    if (recorderRef.current || !mimeType) {
      return;
    }

    // Made before the microphone prompt, while this is still the click: Safari
    // only lets audio run that a user gesture started, and leaves it silent -
    // flat bars - otherwise
    const audioContext =
      typeof AudioContext === "undefined" ? null : new AudioContext();
    audioContextRef.current = audioContext;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream, { mimeType });

      if (audioContext) {
        const level = audioContext.createAnalyser();
        level.fftSize = 1024;
        audioContext.createMediaStreamSource(stream).connect(level);
        setAnalyser(level);
      }

      chunksRef.current = [];
      keepRef.current = false;
      recorderRef.current = recorder;

      recorder.ondataavailable = (event) => {
        if (event.data.size) {
          chunksRef.current.push(event.data);
        }
      };

      recorder.onstop = () => {
        const chunks = chunksRef.current;
        const keep = keepRef.current;

        cleanup();

        if (!keep || !chunks.length) {
          return;
        }

        // Sent exactly as recorded: the API converts it to the Ogg/Opus that
        // WhatsApp renders as a voice note, which no browser records directly
        onFinishRef.current(
          new File(
            [new Blob(chunks, { type: mimeType })],
            `voice-message-${Date.now()}.${mimeType.includes("mp4") ? "mp4" : "webm"}`,
            { type: mimeType.split(";")[0] },
          ),
        );
      };

      recorder.onerror = () =>
        onErrorRef.current(new Error("Recording failed"));

      // Browsers only hand over the microphone from a user gesture, so this
      // has to stay on the click path
      recorder.start();

      setRecording(true);
      setSeconds(0);

      tickRef.current = setInterval(
        () => setSeconds((current) => current + 1),
        1000,
      );
    } catch (error) {
      cleanup();
      onErrorRef.current(error);
    }
  }, [cleanup, mimeType]);

  // Sent on the ceiling from here rather than from inside the tick, so a state
  // updater never has a side effect hanging off it
  useEffect(() => {
    if (recording && seconds >= MAX_RECORDING_SECONDS) {
      stop(true);
    }
  }, [recording, seconds, stop]);

  // A recording left running when the chat closes would hold the microphone
  useEffect(() => cleanup, [cleanup]);

  const send = useCallback(() => stop(true), [stop]);
  const cancel = useCallback(() => stop(false), [stop]);

  return {
    supported: Boolean(mimeType),
    recording,
    seconds,
    analyser,
    start,
    send,
    cancel,
  };
};
